import "./App.css";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { filterSpots, spotDistanceFilter } from "./Helpers/spotFilterer";
import moment, { Moment } from "moment";

import Axios from "axios";
import BestSpots from "./UIComponents/BestSpots/BestSpots";
import DayPicker from "./UIComponents/Weather/DayPicker";
import FilterPicker from "./UIComponents/BestSpots/FilterPicker";
import { LeafletProvider } from "./hooks/useLeaflet";
import { Map } from "react-leaflet";
import MapLayers from "./MapLayers";
import MapTiles from "./MapTiles";
import ShowButton from "./UIComponents/ShowButton";
import Sidebar from "./UIComponents/Sidebar";
import { mapWindDegrees } from "./weatherHelper";
import { useLanguage } from "./hooks/useLanguage";
import { findClosestSpot } from "./Helpers/findClosestSpot";
import { SidebarFactory } from "./SidebarFactory";

const L: any = require("leaflet");

L.esri = require("esri-leaflet");

const labels = {
  all: {
    da: "Alle",
    en: "All",
    de: "Alles",
  },
};

/**
 * Main app for the entire application.
 */
const App = () => {
  const language = useLanguage();
  const map = useRef(null);

  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      zoom: 8,
      showBestSpots: false,
      spots: [],
      distanceFiltered: false,
      showParking: false,
      showFredning: false,
      showLines: false,
      showAreaVideos: false,
      clearScreen: false,
      leftSidebar: null,
      settings: {
        windSelected: true,
        seasonSelected: true,
        fishType: "Alle",
        coastType: "Alle",
      },
    }
  );

  const currentSpot = useMemo(
    () =>
      state.spots.find(
        (s) => state.currentWeather && s.id === state.currentWeather.id
      ),
    [state.spots, state.currentWeather]
  );

  const initialMapPosition: L.LatLngExpression = [
    56.5110135956507,
    9.500000000000009,
  ];

  const [center, setCenter] = useState(initialMapPosition);

  //Loading data for spots:
  useGetSpots((spots) => {
    setState({ spots });
  });

  //Set your location & resize body listener
  useEffect(() => {
    //set your location
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({
          yourLocation: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
        });
      },
      (PositionError) => console.log(PositionError)
    );

    window.addEventListener("resize", () => {
      document.body.style.height = window.innerHeight + "px";
    });

    setTimeout(() => {
      document.body.style.height = window.innerHeight + "px";
    }, 100);
  }, []);

  useEffect(() => {
    const settings = state.settings;
    settings.fishType = labels.all[language];
    settings.coastType = labels.all[language];

    setState({ settings });
  }, [language]);

  useEffect(() => {
    //Select spot if url has spot
    const urlSpot = () => {
      if (window.location.hash) {
        let hashArray = window.location.hash.split("#");
        hashArray.shift();
        let spotNr;
        hashArray.forEach((h) => {
          let keyVal = h.split("=");
          if (keyVal.length === 1) spotNr = parseInt(keyVal[0]) - 1;
        });

        if (spotNr > -1 && spotNr < 151) {
          selectSpotFromList(state.spots[spotNr]);
        }
      }
    };

    if (state.spots.length > 0) {
      urlSpot();
    }

    window.onhashchange = () => {
      urlSpot();
    };

    return () => {
      window.onhashchange = null;
    };
  }, [state.spots]);

  const getCategories = () => {
    //init categories fra fiskepladser
    let coastCategories = [
      labels.all[language],
      ...Array.from(new Set(state.spots.map((d) => d.coastType[language]))),
    ];
    let fishCategories = [
      labels.all[language],
      ...Array.from(
        new Set(
          state.spots.reduce(
            (prev, cur) => [...prev, ...cur.fishTypes[language]],
            []
          )
        )
      ),
    ];
    return { coastCategories, fishCategories };
  };

  const zoomHandler = (e) => {
    let zoom = e.target._zoom;
    zoom > 11 && setState({ showLines: true });
    zoom < 12 && setState({ showLines: false });
    // //vis fredningsZoner når over 11 i zoom
    zoom > 10 && setState({ showFredning: true });
    zoom < 11 && setState({ showFredning: false });
    // show creeks when zoom > 10 and
    zoom > 10 && setState({ showCreeks: true });
    zoom < 11 && setState({ showCreeks: false });
    //vis/hide parkingmarkers
    zoom > 12 && setState({ showParking: true });
    zoom < 13 && setState({ showParking: false });

    //show area videos
    zoom > 9 && setState({ showAreaVideos: true });
    zoom < 10 && setState({ showAreaVideos: false });
  };

  const selectSpot = (spotLayer) => {
    //select spot fra spot marker
    flyTo(map.current.leafletElement, spotLayer._latlng, 13);

    const spot = state.spots.find(
      (s) => s.id === spotLayer.feature.properties.NY_ID
    );
    if (!spot) return;

    const data = {
      ...spot,
      weather: spotWeathers.find((w) => w.id === spot.id),
    };

    if (window.innerWidth < 800) {
      setState({
        showBestSpots: false,
      });
      setTimeout(() => {
        setState({
          leftSidebar: {
            type: "spot",
            data,
          },
        });
      }, 800);
    } else {
      setState({
        leftSidebar: {
          type: "spot",
          data,
        },
      });
    }
  };

  const selectSpotFromList = (spot) => {
    //når spot bliver selected fra bestspotList
    flyTo(
      map.current.leafletElement,
      { lat: spot.gps[0].lat, lng: spot.gps[0].long },
      13
    );

    if (window.innerWidth < 800) {
      setState({ showBestSpots: false });
      setTimeout(() => {
        setState({
          leftSidebar: {
            type: "spot",
            data: spot,
          },
        });
      }, 800);
    } else {
      setState({
        leftSidebar: {
          type: "spot",
          data: spot,
        },
      });
    }
  };

  const onClose = (side) => {
    setState({ clearScreen: false });
    side === "left" &&
      setState({
        leftSidebar: null,
      });

    side === "right" &&
      setState({
        showBestSpots: false,
      });
  };

  const onOpen = () => {
    if (window.innerWidth < 800) {
      //hvis small, clear screen.
      setState({ clearScreen: true });
    }
  };

  const [datetime, setDatetime] = useState(null);
  const handleChangeTime = useCallback(
    async (day: moment.Moment, hours: number) => {
      // datetime format: 2023-07-15T00:00:00.000Z
      setDatetime(
        moment(day)
          .set("hours", hours)
          .set("minutes", 0)
          .set("seconds", 0)
          .set("milliseconds", 0)
      );
    },
    []
  );

  //fetch weather for all spots
  const { spotWeathers, weatherLoading } = useWeather(datetime);

  //Update current weather based on center on move
  useEffect(() => {
    const [lat, lng] = center;

    if (!spotWeathers.length || !state.spots.length) return;
    if (!lat || !lng) return;

    const spot = findClosestSpot(state.spots, lat, lng);
    const weather = spotWeathers.find((w) => w.id === spot.id);

    if (!weather) {
      setState({
        currentWeather: null,
      });
      return;
    }

    setState({
      currentWeather: {
        ...weather,
        ...mapWindDegrees({
          wind: {
            speed: weather.windSpeed,
            deg: weather.windDirection,
          },
          main: {
            temp: weather.temp,
          },
          weather: [{}],
        }),
      },
    });
  }, [datetime, center, spotWeathers, state.spots]);

  const filteredSpots = useMemo(() => {
    if (
      state.yourLocation &&
      !state.distanceFiltered &&
      state.spots.length > 0
    ) {
      spotDistanceFilter(state.spots, state.yourLocation);
      setState({ distanceFiltered: true }); //sørg for at den ikke filter distance hver gang
    }

    if (!state.currentWeather) {
      return [];
    }

    const spots = filterSpots(
      spotWeathers,
      state.spots,
      state.futureDate,
      state.futureMode,
      state.settings,
      language
    );

    return spots;
  }, [
    state.spots,
    state.futureDate,
    state.futureMode,
    state.settings,
    state.currentWeather,
    language,
  ]);

  const { coastCategories, fishCategories } = getCategories();

  return (
    <div style={{ overflow: "hidden", position: "relative", height: "100%" }}>
      <div id="sidebar-container" />

      <Sidebar //right sidebar
        collapsed={!state.showBestSpots}
        onOpen={onOpen}
        onClose={onClose}
        position="right"
      >
        <BestSpots
          language={language}
          spots={filteredSpots}
          settings={state.settings}
          newSettings={(newSettings) => {
            setState({ settings: newSettings });
          }}
          openSpot={(data) => {
            selectSpotFromList(data);
          }} //hvis man click'er på et spot i listen
          coastCategories={coastCategories}
          fishCategories={fishCategories}
          windSpeed={state.currentWeather ? state.currentWeather.windSpeed : 6}
          futureMode={state.futureMode}
        />
      </Sidebar>

      <ShowButton
        hide={state.clearScreen || state.showBestSpots}
        show={() => setState({ showBestSpots: true })}
        pointLeft={true}
      />
      <FilterPicker
        settings={state.settings}
        newSettings={(newSettings) => {
          setState({ settings: newSettings });
        }}
        display={!state.showBestSpots}
        coastCategories={coastCategories}
        fishCategories={fishCategories}
        windSpeed={state.currentWeather ? state.currentWeather.windSpeed : 6}
        futureMode={state.futureMode}
        language={language}
      />

      <Map
        ref={(ref) => {
          map.current = ref;
        }}
        center={initialMapPosition}
        zoom={state.zoom}
        minZoom={7}
        maxZoom={18}
        style={{ height: "100%" }}
        onzoomend={zoomHandler}
        maxBounds={[[57.886133, 6.292162], [54.423851, 14.51592]]}
        onmoveend={(e) => {
          const center = e.target.getCenter();
          setCenter([center.lat, center.lng]);
        }}
      >
        <LeafletProvider>
          <Sidebar //Left Sidebar
            collapsed={!state.leftSidebar}
            onOpen={onOpen}
            onClose={onClose}
            position="left"
          >
            {state.leftSidebar && (
              <SidebarFactory
                type={state.leftSidebar.type}
                data={state.leftSidebar.data}
                currentTime={datetime}
              />
            )}
          </Sidebar>

          <MapLayers
            display={!state.clearScreen}
            spots={state.spots}
            showLines={state.showLines}
            showParking={state.showParking}
            showFredning={state.showFredning}
            showCreeks={state.showCreeks}
            showAreaVideos={state.showAreaVideos}
            selectSpot={selectSpot}
            filteredSpots={filteredSpots}
            yourLocation={state.yourLocation}
            language={language}
            futureDate={state.futureDate}
            openSidebar={(type, feature) => {
              setState({
                leftSidebar: {
                  type,
                  data: feature,
                },
              });
            }}
          />

          <MapTiles
            display={
              !(state.clearScreen || state.showBestSpots || state.smallWeather)
            }
            language={language}
          />

          <DayPicker
            display={!(state.leftSidebar || state.clearScreen)}
            weather={state.currentWeather}
            weatherLoading={weatherLoading}
            setSmallWeather={(val) => {
              setState({ smallWeather: val });
            }}
            onChangeTime={handleChangeTime}
            setFutureMode={(fMode, fDate) =>
              setState({ futureMode: fMode, futureDate: fDate })
            }
            language={language}
            spotName={currentSpot ? currentSpot.name : ""}
          />
        </LeafletProvider>
      </Map>
    </div>
  );
};

export default App;

const useWeather = (datetime: Moment) => {
  //fetch weather for all spots
  const [spotWeathers, setSpotWeathers] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    if (!datetime) return;

    setWeatherLoading(true);
    fetchSpotsWeathers(datetime)
      .then((weathers) => {
        setSpotWeathers(weathers);
        setWeatherLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setWeatherLoading(false);
      });
  }, [datetime]);
  return { spotWeathers, weatherLoading };
};

const weatherCache = {};

function isDST(d) {
  let jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
  let jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) !== d.getTimezoneOffset();
}

/**
 *Load the weather for all spots for a given datetime
 * Weather is loaded from the weather API (which is a proxy to DMI's weather API)
 * @param curTime The datetime for which to load the weather
 * @returns A promise that resolves to an array of weather data for all spots
 */

const fetchSpotsWeathers = (curTime: Moment) => {
  const datetime = curTime
    .clone()
    .utc()
    .add(isDST(new Date()) ? 2 : 1, "hours")
    .format();

  const requestOptions: RequestInit = {
    method: "GET",
    headers: new Headers(),
    redirect: "follow",
  };

  const url = new URL(
    process.env.REACT_APP_WEATHER_API ||
      "https://seahorse-app-ey68o.ondigitalocean.app/"
  );

  const params = new URLSearchParams({
    datetime,
  });

  url.search = params.toString();
  if (weatherCache[url.search]) {
    return Promise.resolve(weatherCache[url.search]);
  }

  return fetch(url, requestOptions)
    .then((response) => response.text())
    .then((data) => {
      const result = JSON.parse(data);
      weatherCache[url.search] = result;
      return result;
    })
    .catch((e) => {
      console.log("error", e);
      return [];
    });
};

/**
 * Load the spots from Umbraco
 */
const useGetSpots = (onSpotsLoaded: (spots: any) => void) => {
  useEffect(() => {
    Axios.get("https://www.limfjordsraadet.dk/data/fiskepladser", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }).then((res: any) => {
      if (typeof res.data === "string") res.data = JSON.parse(res.data);

      const spots = res.data
        .filter((s) => !!s.gps[0] || !!s.weatherStation[0]) //verify spot is valid..
        .map((s) => {
          s.coastType = {
            da: s.coastType,
            de: s.coastType_de,
            en: s.coastType_en,
          };
          s.description = {
            da: s.description,
            de: s.description_de,
            en: s.description_en,
          };
          s.fishTypes = {
            da: s.fishTypes,
            de: s.fishTypes_de,
            en: s.fishTypes_en,
          };
          const ws = s.weatherStation && s.weatherStation[0];
          //make sure all spots have a weatherGPS:
          s.weatherStation = ws
            ? {
                lat: ws.lat,
                lng: ws.long,
              }
            : {
                lat: s.gps[0].lat,
                lng: s.gps[0].long,
              };

          return s;
        });

      onSpotsLoaded(spots);
    });
  }, []);
};

//flyto with disabled map animation during fly
const flyTo = (map, latlng, zoom) => {
  // Disable map interactions
  map.dragging.disable();
  map.touchZoom.disable();
  map.doubleClickZoom.disable();
  map.scrollWheelZoom.disable();
  map.boxZoom.disable();
  map.keyboard.disable();

  map.flyTo(latlng, zoom, {
    animate: true,
    duration: 0.8,
    easeLinearity: 0,
  });

  // Re-enable map interactions
  setTimeout(() => {
    map.dragging.enable();
    map.touchZoom.enable();
    map.doubleClickZoom.enable();
    map.scrollWheelZoom.enable();
    map.boxZoom.enable();
    map.keyboard.enable();
  }, 800);
};
